import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Card, Spinner, Alert, ButtonGroup, Button, 
  Form, InputGroup, Table, Badge, Modal 
} from 'react-bootstrap';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  FaUsers, FaChartLine, FaSearch, FaSignInAlt, FaClock, 
  FaLayerGroup, FaEye, FaDownload, FaChartPie
} from 'react-icons/fa';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAdminGuard } from '../../../hooks/useAdminGuard';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

const ActivityDashboardMejorado = () => {
  useAdminGuard(3); // Requiere AdminLevel 3

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7');
  const [dashboardData, setDashboardData] = useState(null);
  
  // Estados para búsqueda
  const [searchLegajo, setSearchLegajo] = useState('');
  const [userLogs, setUserLogs] = useState([]);
  const [loadingUser, setLoadingUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  const fetchDashboardData = async (days = 7) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/activity-logs/dashboard?days=${days}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}`);
      }

      const data = await response.json();
      setDashboardData(data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError('Error al cargar el dashboard. Verifique que la tabla user_activity_logs exista.');
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  const searchByLegajo = async () => {
    if (!searchLegajo.trim()) return;

    setLoadingUser(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/activity-logs?user_legajo=${searchLegajo}&limit=100`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Error al buscar logs');

      const data = await response.json();
      setUserLogs(data.data || []);
      
      // Calcular métricas del usuario
      if (data.data && data.data.length > 0) {
        calculateUserMetrics(data.data);
      }
    } catch (err) {
      console.error('Error buscando logs:', err);
      Alert('Error al buscar actividad del usuario');
    } finally {
      setLoadingUser(false);
    }
  };

  const calculateUserMetrics = (logs) => {
    const acciones = {};
    const porDia = {};
    const porHora = {};

    logs.forEach(log => {
      // Contar acciones
      acciones[log.action] = (acciones[log.action] || 0) + 1;

      // Por día
      const fecha = format(new Date(log.created_at), 'yyyy-MM-dd');
      porDia[fecha] = (porDia[fecha] || 0) + 1;

      // Por hora
      const hora = new Date(log.created_at).getHours();
      porHora[hora] = (porHora[hora] || 0) + 1;
    });

    setSelectedUser({
      legajo: searchLegajo,
      nombre: logs[0]?.user_name || 'Usuario',
      totalAcciones: logs.length,
      acciones: Object.entries(acciones).map(([k, v]) => ({ accion: k, total: v })),
      porDia: Object.entries(porDia).map(([k, v]) => ({ fecha: k, total: v })),
      porHora: Object.entries(porHora).map(([k, v]) => ({ hora: k, total: v })),
      ultimaActividad: logs[0]?.created_at
    });
    
    setShowUserModal(true);
  };

  useEffect(() => {
    fetchDashboardData(timeRange);
  }, [timeRange]);

  const COLORS = ['#667eea', '#00D9FF', '#FEB019', '#00E396', '#FF4560', '#775DD0'];

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando dashboard...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">{error}</Alert>
        <Alert variant="info">
          <strong>Nota:</strong> Si la tabla user_activity_logs no existe, los logs no se están guardando.
        </Alert>
      </Container>
    );
  }

  const totalAcciones = dashboardData?.userMetrics?.total_acciones || 0;
  const usuariosActivos = dashboardData?.userMetrics?.usuarios_unicos || 0;
  const promedioAcciones = dashboardData?.userMetrics?.promedio_acciones_por_usuario || 0;

  return (
    <Container fluid className="py-4">
      {/* Header con búsqueda */}
      <Row className="mb-4">
        <Col md={6}>
          <h2 className="mb-1">
            <FaChartLine className="me-2" style={{ color: '#667eea' }} />
            Dashboard de Actividades
          </h2>
          <p className="text-muted">Monitoreo y análisis de actividad del sistema</p>
        </Col>
        <Col md={3}>
          <ButtonGroup size="sm">
            {['7', '15', '30'].map(days => (
              <Button 
                key={days}
                variant={timeRange === days ? 'primary' : 'outline-primary'}
                onClick={() => setTimeRange(days)}
              >
                {days} días
              </Button>
            ))}
          </ButtonGroup>
        </Col>
        <Col md={3}>
          <InputGroup size="sm">
            <Form.Control
              type="text"
              placeholder="Buscar por legajo..."
              value={searchLegajo}
              onChange={(e) => setSearchLegajo(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchByLegajo()}
            />
            <Button variant="primary" onClick={searchByLegajo} disabled={loadingUser}>
              {loadingUser ? <Spinner size="sm" animation="border" /> : <FaSearch />}
            </Button>
          </InputGroup>
        </Col>
      </Row>

      {/* Cards de métricas principales */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Total Acciones</h6>
                  <h3 className="mb-0">{totalAcciones.toLocaleString()}</h3>
                </div>
                <div className="p-3 bg-primary bg-opacity-10 rounded">
                  <FaChartLine size={24} className="text-primary" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Usuarios Activos</h6>
                  <h3 className="mb-0">{usuariosActivos}</h3>
                </div>
                <div className="p-3 bg-success bg-opacity-10 rounded">
                  <FaUsers size={24} className="text-success" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Promedio / Usuario</h6>
                  <h3 className="mb-0">{promedioAcciones}</h3>
                </div>
                <div className="p-3 bg-warning bg-opacity-10 rounded">
                  <FaChartPie size={24} className="text-warning" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Período</h6>
                  <h3 className="mb-0">{timeRange} días</h3>
                </div>
                <div className="p-3 bg-info bg-opacity-10 rounded">
                  <FaClock size={24} className="text-info" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Gráficos principales */}
      <Row className="mb-4">
        <Col md={8}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0"><FaChartLine className="me-2" />Actividad por Día</h5>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dashboardData?.activityByDay || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="fecha" 
                    tickFormatter={(date) => format(new Date(date), 'dd/MM')}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(date) => format(new Date(date), 'dd/MM/yyyy')}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    stroke="#667eea" 
                    strokeWidth={2}
                    name="Acciones"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0"><FaLayerGroup className="me-2" />Por Categoría</h5>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={dashboardData?.actionCategories || []}
                    dataKey="total"
                    nameKey="categoria"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {(dashboardData?.actionCategories || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top usuarios y acciones */}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0"><FaUsers className="me-2" />Top 10 Usuarios Más Activos</h5>
            </Card.Header>
            <Card.Body>
              <Table hover size="sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th className="text-end">Acciones</th>
                    <th className="text-end">Días</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboardData?.topUsuarios || []).map((user, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{user.nombre}</td>
                      <td>
                        <Badge bg={user.tipo_usuario === 'Admin' ? 'primary' : 'secondary'}>
                          {user.tipo_usuario}
                        </Badge>
                      </td>
                      <td className="text-end fw-bold">{user.total_acciones}</td>
                      <td className="text-end">{user.dias_activos}</td>
                      <td>
                        <Button 
                          size="sm" 
                          variant="outline-primary"
                          onClick={() => {
                            setSearchLegajo(user.user_legajo);
                            setTimeout(() => searchByLegajo(), 100);
                          }}
                        >
                          <FaEye />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0"><FaChartLine className="me-2" />Top 10 Acciones</h5>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={dashboardData?.topAcciones || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis 
                    dataKey="accion" 
                    type="category" 
                    width={150}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip />
                  <Bar dataKey="total" fill="#667eea" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Actividad por hora */}
      <Row className="mb-4">
        <Col md={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0"><FaClock className="me-2" />Actividad por Hora del Día</h5>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dashboardData?.activityByHour || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="hora" 
                    tickFormatter={(hora) => `${hora}:00`}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(hora) => `${hora}:00 hrs`}
                  />
                  <Bar dataKey="total" fill="#00D9FF" name="Acciones" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de detalles del usuario */}
      <Modal show={showUserModal} onHide={() => setShowUserModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaUsers className="me-2" />
            Detalle de Actividad - {selectedUser?.nombre}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <Row className="mb-3">
                <Col>
                  <strong>Legajo:</strong> {selectedUser.legajo}
                </Col>
                <Col>
                  <strong>Total Acciones:</strong> {selectedUser.totalAcciones}
                </Col>
                <Col>
                  <strong>Última Actividad:</strong>{' '}
                  {format(new Date(selectedUser.ultimaActividad), 'dd/MM/yyyy HH:mm', { locale: es })}
                </Col>
              </Row>

              <h6 className="mt-3">Top Acciones del Usuario</h6>
              <Table size="sm" striped>
                <thead>
                  <tr>
                    <th>Acción</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedUser.acciones.slice(0, 10).map((acc, idx) => (
                    <tr key={idx}>
                      <td>{acc.accion}</td>
                      <td className="text-end">{acc.total}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <h6 className="mt-3">Actividad por Día</h6>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={selectedUser.porDia}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fecha" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="total" fill="#667eea" />
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUserModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ActivityDashboardMejorado;
